/**
 * Quote arithmetic (TJR inventory 01 §2.7.2; MB decisions 124–126).
 *
 * Line km follow TJR so old quotes give the same figures (decision 124, bug B13 kept on purpose):
 *   traverse_km = round(Σ traverse line lengths / 1000), tie_km = round(Σ tie line lengths / 1000),
 *   line_km = traverse_km + tie_km, tie spacing = 10 × traverse spacing,
 * with Delphi's `Round` (halves to the even neighbour). Price per area = line km × rate per line km; the survey price
 * = mobilisation + Σ area prices. Data merge (merging the new survey with an old existing survey data set) is a
 * separate optional item outside the total (decision 167). TASP is an estimate the client pays to the authority,
 * standby is per day and fuel per litre or at cost: none of them is in the total either.
 * Tax is only wording ("exclusive of GST") unless the tenant turns the tax line on (decision 125).
 * All money is summed in whole cents.
 */
import type { ValidationIssue } from './model.js';
import type { PlanFileLine } from './plan-files.js';
import type {
  ChargeKind,
  CommercialTerms,
  Currency,
  QuoteAggregate,
  QuoteArea,
  QuoteCharge,
  TenantProfile,
} from './quote-model.js';
import { CURRENCY_LABEL } from './quote-model.js';

/** Delphi `Round`: nearest integer, halves to the even neighbour (TJR is a Delphi program). */
export function delphiRound(x: number): number {
  const f = Math.floor(x);
  const d = x - f;
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

export interface AreaKm {
  readonly traverse_km: number;
  readonly tie_km: number;
  readonly line_km: number;
  readonly traverse_count: number;
  readonly tie_count: number;
}

/** TJR's line km of one area from its lines (rounded per kind, then added). */
export function tjrAreaKm(lines: readonly Pick<PlanFileLine, 'kind' | 'length_m'>[]): AreaKm {
  let trav = 0;
  let tie = 0;
  let travN = 0;
  let tieN = 0;
  for (const l of lines) {
    if (l.kind === 'tie') {
      tie += l.length_m;
      tieN += 1;
    } else {
      trav += l.length_m;
      travN += 1;
    }
  }
  const traverse_km = delphiRound(trav / 1000);
  const tie_km = delphiRound(tie / 1000);
  return { traverse_km, tie_km, line_km: traverse_km + tie_km, traverse_count: travN, tie_count: tieN };
}

/** TJR assumes ties at ten times the traverse spacing (`FUN_006f2d34`). */
export function tjrTieSpacing(travSpacingM: number): number {
  return 10 * travSpacingM;
}

type AreaKmFields = Pick<QuoteArea, 'traverse_km' | 'tie_km' | 'line_km_override' | 'lines_file'>;

/** True when the area's km were computed from an imported lines file (`.lns`, or a `.sum` with its line list). */
export function hasLinesFile(a: Pick<QuoteArea, 'traverse_km' | 'tie_km' | 'lines_file'>): boolean {
  return a.lines_file.trim() !== '' && a.traverse_km + a.tie_km > 0;
}

/**
 * Line km of an area as quoted (decision 169): computed from its lines file whenever it has one, never typed. A
 * typed or imported figure is used only for an area without a lines file, and is otherwise only compared
 * (`lineKmCheck`).
 */
export function areaLineKm(a: AreaKmFields): number {
  if (hasLinesFile(a)) return a.traverse_km + a.tie_km;
  return a.line_km_override ?? 0;
}

export interface AreaKmCheck {
  readonly area_id: string;
  readonly name: string;
  /** Km from the lines file; null when the area has none. */
  readonly lines_km: number | null;
  /** The typed or imported figure; null when there is none. */
  readonly typed_km: number | null;
  /** What the quote and contract print. */
  readonly quoted_km: number;
  readonly source: 'lines_file' | 'typed' | 'none';
  /** typed − lines file, when both exist and differ. */
  readonly difference_km: number | null;
}

export interface LineKmCheck {
  readonly areas: readonly AreaKmCheck[];
  /** Σ of the rows: the only total ever printed. */
  readonly total_km: number;
  /** A typed or imported total (e.g. the total printed on a TJR quote or contract), or null. */
  readonly stated_total_km: number | null;
  /** stated − Σ rows, when it differs. */
  readonly total_difference_km: number | null;
  /** In words, one per flag; warnings, not send blockers (the computed figures are used). */
  readonly flags: readonly ValidationIssue[];
}

const kmWords = (km: number): string => `${Math.round(km).toLocaleString('en-AU')} line km`;
const diffWords = (d: number): string =>
  `${Math.abs(d).toLocaleString('en-AU')} km ${d > 0 ? 'more' : 'less'}`;

/**
 * The line-km check of decision 169: each area's km from its lines file, the total as the sum of the rows, and a
 * flag wherever a typed or imported figure (an area's typed km, or a stated total) differs, or an area has no lines
 * file.
 */
export function lineKmCheck(
  areas: ReadonlyArray<AreaKmFields & Pick<QuoteArea, 'id' | 'name' | 'sort'>>,
  statedTotalKm: number | null,
): LineKmCheck {
  const rows = [...areas]
    .sort((a, b) => a.sort - b.sort)
    .map((a): AreaKmCheck => {
      const lines = hasLinesFile(a) ? a.traverse_km + a.tie_km : null;
      const typed = a.line_km_override;
      return {
        area_id: a.id,
        name: a.name,
        lines_km: lines,
        typed_km: typed,
        quoted_km: areaLineKm(a),
        source: lines !== null ? 'lines_file' : typed !== null ? 'typed' : 'none',
        difference_km: lines !== null && typed !== null && typed !== lines ? typed - lines : null,
      };
    });
  const total = rows.reduce((s, r) => s + r.quoted_km, 0);
  const flags: ValidationIssue[] = [];
  for (const r of rows) {
    const name = r.name || 'An area';
    if (r.difference_km !== null) {
      flags.push({
        field: `area.${r.area_id}.line_km_override`,
        message: `${name}: the lines file gives ${kmWords(r.lines_km!)}; the typed figure ${kmWords(r.typed_km!)} is ${diffWords(r.difference_km)}. The quote uses the lines file.`,
      });
    } else if (r.source === 'typed') {
      flags.push({
        field: `area.${r.area_id}.lines_file`,
        message: `${name}: ${kmWords(r.typed_km!)} typed, not computed. Import the area's lines file (.lns) so the km are computed from it.`,
      });
    }
  }
  const totalDiff = statedTotalKm !== null && statedTotalKm !== total ? statedTotalKm - total : null;
  if (totalDiff !== null) {
    flags.push({
      field: 'stated_total_line_km',
      message: `The areas add up to ${kmWords(total)}; the stated total ${kmWords(statedTotalKm!)} is ${diffWords(totalDiff)}. The quote prints the sum of the areas.`,
    });
  }
  return {
    areas: rows,
    total_km: total,
    stated_total_km: statedTotalKm,
    total_difference_km: totalDiff,
    flags,
  };
}

export const toCents = (dollars: number): number => Math.round(dollars * 100);

/** Price of an area in cents; null when it has no rate yet. */
export function areaPriceCents(a: AreaKmFields & Pick<QuoteArea, 'rate_per_line_km'>): number | null {
  if (a.rate_per_line_km === null) return null;
  return toCents(a.rate_per_line_km) * areaLineKm(a);
}

export function charge(charges: readonly QuoteCharge[], kind: ChargeKind): QuoteCharge | null {
  return charges.find((c) => c.kind === kind) ?? null;
}

export interface AreaTotal {
  readonly name: string;
  readonly line_km: number;
  readonly rate_cents: number | null;
  readonly price_cents: number | null;
}

export interface QuoteTotals {
  readonly areas: readonly AreaTotal[];
  readonly total_line_km: number;
  readonly mobilisation_cents: number | null;
  readonly acquisition_cents: number | null;
  /** The optional data merge (decision 167): quoted separately, never part of the total. */
  readonly data_merge_cents: number | null;
  /** Mobilisation + areas, ex tax; null while any area has no rate. */
  readonly total_ex_tax_cents: number | null;
  /** Only when the tenant shows a tax line. */
  readonly tax_cents: number | null;
  readonly total_inc_tax_cents: number | null;
  readonly tasp_estimate_cents: number | null;
  readonly standby_per_day_cents: number | null;
  readonly standby_cap_days: number | null;
  readonly fuel_per_litre_cents: number | null;
}

const cents = (c: QuoteCharge | null): number | null => (c && c.amount !== null ? toCents(c.amount) : null);

export function quoteTotals(
  agg: Pick<QuoteAggregate, 'areas' | 'charges'>,
  tax: Pick<TenantProfile, 'tax_line_enabled' | 'tax_rate_pct'> | null,
): QuoteTotals {
  const areas = [...agg.areas]
    .sort((a, b) => a.sort - b.sort)
    .map((a) => ({
      name: a.name,
      line_km: areaLineKm(a),
      rate_cents: a.rate_per_line_km === null ? null : toCents(a.rate_per_line_km),
      price_cents: areaPriceCents(a),
    }));
  const acquisition =
    areas.length > 0 && areas.every((a) => a.price_cents !== null)
      ? areas.reduce((s, a) => s + (a.price_cents ?? 0), 0)
      : null;
  const mob = cents(charge(agg.charges, 'mobilisation'));
  const merge = cents(charge(agg.charges, 'data_merge'));
  const total = acquisition === null ? null : acquisition + (mob ?? 0);
  const taxOn = tax?.tax_line_enabled === true;
  const taxCents = taxOn && total !== null ? Math.round((total * (tax?.tax_rate_pct ?? 0)) / 100) : null;
  const fuel = charge(agg.charges, 'fuel');
  const standby = charge(agg.charges, 'standby');
  return {
    areas,
    total_line_km: areas.reduce((s, a) => s + a.line_km, 0),
    mobilisation_cents: mob,
    acquisition_cents: acquisition,
    data_merge_cents: merge,
    total_ex_tax_cents: total,
    tax_cents: taxCents,
    total_inc_tax_cents: taxOn && total !== null && taxCents !== null ? total + taxCents : null,
    tasp_estimate_cents: cents(charge(agg.charges, 'tasp')),
    standby_per_day_cents: cents(standby),
    standby_cap_days: standby?.cap_days ?? null,
    fuel_per_litre_cents: fuel?.fuel_mode === 'per_litre' ? cents(fuel) : null,
  };
}

/** "$1,234.56", "US$12.50". */
export function formatMoney(centsValue: number, currency: Currency): string {
  const sign = centsValue < 0 ? '-' : '';
  const abs = Math.abs(centsValue);
  const dollars = Math.floor(abs / 100).toLocaleString('en-AU');
  const c = String(abs % 100).padStart(2, '0');
  return `${sign}${CURRENCY_LABEL[currency].symbol}${dollars}.${c}`;
}

/** "25,483". */
export function formatKmInt(km: number): string {
  return Math.round(km).toLocaleString('en-AU');
}

/**
 * Schedule of payments. TJR accepted PP1 + PP2 ≤ 100 with the balance on delivery (bug B2); Teslyn states the
 * balance explicitly and checks that the three total exactly 100 % (F26048: 50 % + 40 % + 10 %). Any part may be
 * 0 % (decision 168: for example 50/50/0, no balance on delivery); a 0 % part is not printed.
 */
export function paymentBalance(pp1: number | null, pp2: number | null): number | null {
  if (pp1 === null) return null;
  return 100 - pp1 - (pp2 ?? 0);
}

export function validatePaymentSchedule(
  t: Pick<CommercialTerms, 'pp1_pct' | 'pp2_pct' | 'balance_pct'>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const pct = (v: number | null): boolean => v !== null && Number.isInteger(v) && v >= 0 && v <= 100;
  if (t.pp1_pct === null) {
    issues.push({ field: 'pp1_pct', message: 'Enter the first progress payment, paid on signing, in %.' });
    return issues;
  }
  if (!pct(t.pp1_pct))
    issues.push({
      field: 'pp1_pct',
      message: 'The first progress payment must be a whole number from 0 to 100 %.',
    });
  if (t.pp2_pct !== null && !pct(t.pp2_pct)) {
    issues.push({
      field: 'pp2_pct',
      message: 'The second progress payment must be a whole number from 0 to 100 %.',
    });
  }
  if (t.balance_pct === null || !pct(t.balance_pct)) {
    issues.push({
      field: 'balance_pct',
      message: 'The balance on delivery must be a whole number from 0 to 100 %.',
    });
  }
  const sum = (t.pp1_pct ?? 0) + (t.pp2_pct ?? 0) + (t.balance_pct ?? 0);
  if (issues.length === 0 && sum !== 100) {
    issues.push({
      field: 'balance_pct',
      message: `The payments add up to ${sum} %. They must add up to exactly 100 %: first payment, second payment and the balance on delivery.`,
    });
  }
  return issues;
}

/**
 * Average sample distance along line from the aircraft's cruise speed and the sample rate, as the 2026 contract
 * prints it: "approximately 3m samples" (2.1) and "3-4 metres" (2.8) for a Grand Caravan at 145 kt and 20 Hz
 * (74.6 m/s ÷ 20 = 3.7 m). Low and high are the distance rounded down and up to one significant figure [inferred
 * from F26048: 3.7 m → 3–4, 37.3 m → 30–40].
 */
export function sampleDistance(
  cruiseKt: number,
  hz: number,
): { readonly low: number; readonly high: number } | null {
  if (!(cruiseKt > 0 && hz > 0)) return null;
  const d = (cruiseKt * 1852) / 3600 / hz;
  const mag = 10 ** Math.floor(Math.log10(d));
  const low = Math.floor(d / mag) * mag;
  return { low, high: low + mag };
}

/** "000-180 (N-S)", "090-270 (E-W)" for a line bearing. */
export function directionLabel(bearingDeg: number): string {
  const a = ((Math.round(bearingDeg) % 180) + 180) % 180;
  const b = a + 180;
  const pad = (n: number): string => String(n).padStart(3, '0');
  const names: Record<number, string> = { 0: 'N-S', 45: 'NE-SW', 90: 'E-W', 135: 'SE-NW' };
  const name = names[a];
  return name ? `${pad(a)}-${pad(b)} (${name})` : `${pad(a)}-${pad(b)}`;
}

/** Downward detector volume: 16.8 L per pack (TJR `*!RadPack x 16.8 litre`; F26048 3 packs = 50.4 L). */
export function detectorLitres(packs: number): number {
  return Math.round(packs * 16.8 * 10) / 10;
}
